<template>
  <button
    type="button"
    class="rcard"
    :class="['rcard--' + roll.level, { 'rcard--selected': selected }]"
    :aria-pressed="selected ? 'true' : 'false'"
    @click="$emit('select', roll)"
  >
    <span class="rcard__top">
      <roll-graphic :share="roll.restShare" :level="roll.level" :size="60" />
      <span class="rcard__head">
        <span class="rcard__titlerow">
          <span class="rcard__title">Rolle {{ roll.id }}</span>
          <span class="rcard__state" :class="'rcard__state--' + roll.level">{{ stateText }}</span>
        </span>
        <span class="rcard__sub">{{ printerText }}</span>
        <span class="rcard__sub">Abschnitt {{ rangeText }} · {{ roll.covered }} belegt</span>
      </span>
    </span>
    <roll-section-band :ranges="roll.ranges" :gaps="roll.gaps" :to="roll.capacity" :marker="roll.max" />
    <span class="rcard__facts">
      <span><strong>{{ roll.rest }}</strong> übrig (≈ {{ meters }} m){{ ticketText }}</span>
      <span class="rcard__time" :title="exact">{{ lastText }}</span>
    </span>
    <span v-for="(w, i) in roll.warnings" :key="i" class="rcard__warn" :class="'rcard__warn--' + w.level">
      <app-icon :name="w.level === 'info' ? 'info' : 'alertTriangle'" :size="13" />{{ w.text }}
    </span>
  </button>
</template>

<script>
// Karte einer Rolle im Einsatz (Rollen, Kundenwunsch 27.10.2026): gezeichnete Rolle mit Rest, Rollennummer und Zustand,
// Drucker, belegter Abschnittsbereich, Abschnittsband (0 bis Kapazität, Lücken markiert), Rest in Abschnitten und
// Metern, grob verbleibende Verkäufe, letzter Druck und die Hinweise der Auswertung. Ein Klick öffnet den Verlauf.
// Props: roll (aus analyzeRolls), printerNames { printer_id: Bemerkung }, selected, now. Ereignis: select.
import RollGraphic from './RollGraphic';
import RollSectionBand from './RollSectionBand';
import { SECTION_MM, ticketsLeft, rangeText } from './rollTracking';
import { relativeTime, exactTime } from '@/utils/relativeTime';

export default {
  name: 'RollCard',
  components: { RollGraphic, RollSectionBand },
  props: {
    roll: { type: Object, required: true },
    printerNames: { type: Object, default: () => ({}) },
    selected: { type: Boolean, default: false },
    now: { type: Date, default: () => new Date() }
  },
  computed: {
    stateText() {
      return { ok: 'okay', warn: 'prüfen', error: 'Lücke' }[this.roll.level] || '';
    },
    rangeText() {
      return rangeText(this.roll.min, this.roll.max);
    },
    printerText() {
      const current = this.roll.activeOn[0];
      const name = current && this.printerNames[current];
      const others = this.roll.printers.filter((p) => p !== current);
      return 'Drucker ' + current + (name ? ' · ' + name : '') + (others.length ? ' (vorher ' + others.join(', ') + ')' : '');
    },
    meters() {
      return ((this.roll.rest * SECTION_MM) / 1000).toLocaleString('de-DE', { maximumFractionDigits: 1 });
    },
    ticketText() {
      const n = ticketsLeft(this.roll);
      return n === null ? '' : ' · reicht für ~' + n + ' Verkäufe';
    },
    lastText() {
      return 'letzter Druck ' + relativeTime(this.roll.lastTime, this.now);
    },
    exact() {
      return exactTime(this.roll.lastTime);
    }
  }
};
</script>

<style scoped>
.rcard {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  margin: 0;
  padding: 12px 14px;
  border: 1px solid var(--line);
  border-radius: var(--r-ctl);
  background: var(--surface);
  color: var(--ink);
  font-family: var(--font);
  font-size: 13px;
  line-height: 18px;
  text-align: left;
  cursor: pointer;
}
:where(html:not(.dichte-touch)) .rcard:hover {
  border-color: var(--edge-hover);
}
.rcard:focus {
  outline: none;
}
.rcard:focus-visible {
  box-shadow: 0 0 0 2px var(--link);
}
.rcard--selected {
  border-color: var(--link);
  box-shadow: inset 0 0 0 1px var(--link);
}
.rcard__top {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
.rcard__head {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.rcard__titlerow {
  display: flex;
  align-items: center;
  gap: 8px;
}
.rcard__title {
  font-size: 15px;
  line-height: 20px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.rcard__state {
  padding: 1px 6px;
  border-radius: 9px;
  font-size: 11px;
  line-height: 16px;
  font-weight: 600;
}
.rcard__state--ok {
  background: var(--green-t);
  color: var(--green-fg);
}
.rcard__state--warn {
  background: var(--amber-t);
  color: var(--amber);
}
.rcard__state--error {
  background: var(--red-t);
  color: var(--red);
}
.rcard__sub {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--ink-3);
  font-size: 12px;
  line-height: 17px;
  font-variant-numeric: tabular-nums;
}
.rcard__facts {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 2px 12px;
  color: var(--ink-2);
  font-size: 12px;
  line-height: 17px;
  font-variant-numeric: tabular-nums;
}
.rcard__facts strong {
  color: var(--ink);
  font-weight: 600;
}
.rcard__time {
  color: var(--ink-3);
}
.rcard__warn {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 12px;
  line-height: 17px;
  color: var(--ink-2);
}
.rcard__warn .app-icon {
  flex: none;
  margin-top: 2px;
}
.rcard__warn--error {
  color: var(--red);
}
.rcard__warn--warn .app-icon {
  color: var(--amber);
}
.rcard__warn--info .app-icon {
  color: var(--ink-3);
}
</style>
